import React, { useEffect, useState } from "react";
import {
  doc,
  onSnapshot,
  getDocs,
  collection,
  query,
  where,
  deleteDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import StripeCheckoutButton2025 from "./StripeCheckoutButton2025";
import { db, auth } from "../api/firebase";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Button,
  Typography,
  Box,
  CircularProgress,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom"; // ← 追加

import CelebrationIcon from "@mui/icons-material/Celebration";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";

const PaymentContainer2025Late = () => {
  const paymentPhase = "3";
  const title = "マスターマインドメンバーズ2025八重の誓い";
  const description =
    "マスターマインドの素晴らしい仲間たちと、内面へと静かに降りてゆく88の祈り";

  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rangeLoading, setRangeLoading] = useState(true);
  const [now, setNow] = useState(new Date());
  const [dateRanges, setDateRanges] = useState([]);
  const [hasBankTransferRequest, setHasBankTransferRequest] = useState(false);
  const [bankTransferRequestData, setBankTransferRequestData] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelConfirmed, setCancelConfirmed] = useState(false);
  const [isBankTransferExpired, setBankTransferExpired] = useState(false);
  const [expiredDeadlineDate, setExpiredDeadlineDate] = useState(null);

  const navigate = useNavigate(); // ← 追加


  // 現在時刻を10秒ごとに更新
  useEffect(() => {
    const intervalId = setInterval(() => {
      setNow(new Date());
    }, 10000);
    return () => clearInterval(intervalId);
  }, []);

  // ユーザーデータの取得
  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.error("No authenticated user found.");
      setLoading(false);
      return;
    }

    const userId = currentUser.uid;
    const userDocRef = doc(db, "users", userId);

    const unsubscribe = onSnapshot(
      userDocRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          setUserData({ ...data, userId });
        } else {
          console.error(`User document not found for uid: ${userId}`);
        }
        setLoading(false);
      },
      (error) => {
        console.error("Error setting up snapshot listener:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // 支払いフェーズ情報の取得
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "paymentPhases2025Late"),
      (snap) => {
        const list = snap.docs
          .map((doc) => {
            const data = doc.data();
            return {
              id: doc.id, // ← これを追加！
              ...data,
              start: data.start.toDate(),
              end: data.end ? data.end.toDate() : null,
            };
          })
          .filter((range) => String(range.phaseNumber) === paymentPhase);

        setDateRanges(list);
        setRangeLoading(false);
      },
      (error) => {
        console.error("フェーズ取得エラー:", error);
        setRangeLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    checkBankTransferRequest();
  }, []);

  const checkBankTransferRequest = async () => {
    if (!auth.currentUser) return;

    const q = query(
      collection(db, "bankTransferRequests2025"),
      where("userId", "==", auth.currentUser.uid)
    );
    const snap = await getDocs(q);

    if (snap.empty) {
      setHasBankTransferRequest(false);
      setBankTransferRequestData(null);
      setBankTransferExpired(false);
      setExpiredDeadlineDate(null);
      return;
    }

    // 🔽 requestedAtでソート
    const sortedDocs = snap.docs
      .filter((doc) => doc.data().requestedAt)
      .sort(
        (a, b) => b.data().requestedAt.toDate() - a.data().requestedAt.toDate()
      );

    const latest = sortedDocs[0];
    const latestData = latest.data();

    const requestedAt = latestData.requestedAt?.toDate();
    const deadline = new Date(requestedAt);
    deadline.setDate(deadline.getDate() + 7);

    const isExpired = new Date() > deadline;

    if (latestData.cancel || !requestedAt) {
      // 明示的にキャンセル済み or requestedAtが存在しない
      setHasBankTransferRequest(false);
      setBankTransferRequestData(null);
      setBankTransferExpired(false);
      setExpiredDeadlineDate(null);
    } else if (isExpired) {
      // 期限切れ
      setHasBankTransferRequest(false); // 有効申請ではなくなる
      setBankTransferRequestData(null);
      setBankTransferExpired(true);
      setExpiredDeadlineDate(deadline);
    } else {
      // 有効な申込みとみなす
      setHasBankTransferRequest(true);
      setBankTransferRequestData(latestData);
      setBankTransferExpired(false);
      setExpiredDeadlineDate(null);
    }
  };

  const handleCancelBankTransfer = async () => {
    try {
      const q = query(
        collection(db, "bankTransferRequests2025"),
        where("userId", "==", auth.currentUser.uid)
      );
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        alert("キャンセル対象が見つかりませんでした。");
        return;
      }

      // 最新のrequestedAtがあるドキュメントを探す（cancelがfalseかundefined）
      const validDocs = snapshot.docs
        .map((doc) => ({ id: doc.id, ref: doc.ref, ...doc.data() }))
        .filter((doc) => !doc.cancel && doc.requestedAt)
        .sort((a, b) => b.requestedAt.toDate() - a.requestedAt.toDate());

      if (validDocs.length === 0) {
        alert("キャンセル対象が見つかりませんでした。");
        return;
      }

      const latestDocRef = validDocs[0].ref;

      await updateDoc(latestDocRef, {
        cancel: true,
        canceledAt: serverTimestamp(),
      });

      alert("銀行振込申請をキャンセルしました。");

      // 状態リセット
      setCancelDialogOpen(false);
      setCancelConfirmed(false);
      setHasBankTransferRequest(false);
    } catch (error) {
      console.error("キャンセルエラー:", error);
      alert("キャンセルに失敗しました。時間をおいて再試行してください。");
    }
  };

  // const has2ndGeneration = userData?.payments?.some((p) => p.paymentPhase === "2");
  // useEffect(() => {
  //   if (has2ndGeneration) {
  //     navigate("/home");
  //   }
  // }, [has2ndGeneration, navigate]);


  const payment = userData?.payments?.find((p) => p.paymentPhase === "3");
  const method = payment?.paymentMethod;

  if (method === "銀行振込" || method === "クレジットカード") {
    return (
      <Box
        sx={{
          mt: 4,
          p: 3,
          borderRadius: 2,
          backgroundColor: "#f0f4ff",
          boxShadow: 3,
          textAlign: "center",
          maxWidth: 600,
          mx: "auto",
        }}
      >
        <CelebrationIcon sx={{ fontSize: 40, color: "primary.main", mb: 1 }} />
        <Typography variant="h5" fontWeight="bold" color="primary" gutterBottom>
          「八重の誓い」プロジェクトにご参加いただけます！
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Typography variant="body1">
          すでに本フェーズでのお支払いが確認されております。
        </Typography>
        <Typography variant="body1" sx={{ mt: 1 }}>
          あなたには、「八重の誓い」プロジェクトへの正式な参加権が付与されています。
        </Typography>
        <Typography variant="body1" sx={{ mt: 1 }}>
          共に祈り、共に歩む旅路をどうぞお楽しみください。
        </Typography>
      </Box>
    );
  }

  if (method === "2期無料招待") {
    return (
      <Box
        sx={{
          mt: 4,
          p: 3,
          borderRadius: 2,
          backgroundColor: "#f9fbe7",
          boxShadow: 3,
          textAlign: "center",
          maxWidth: 600,
          mx: "auto",
        }}
      >
        <EmojiEventsIcon sx={{ fontSize: 40, color: "success.main", mb: 1 }} />
        <Typography
          variant="h5"
          fontWeight="bold"
          color="success.main"
          gutterBottom
        >
          無料招待参加ありがとうございます！
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Typography variant="body1">
          あなたは前フェーズからの無料招待者として、「八重の誓い」プロジェクトに登録されています。
        </Typography>
        <Typography variant="body1" sx={{ mt: 1 }}>
          ご継続ありがとうございます。新たな祈りの旅が始まります。
        </Typography>
      </Box>
    );
  }
  if (loading || rangeLoading) return <CircularProgress sx={{ mt: 4 }} />;
  if (!userData) return <div>ユーザー情報を取得できませんでした。</div>;

//   銀行振込申請者の画面
  if (hasBankTransferRequest && bankTransferRequestData?.requestedAt) {
    const requestedAtDate = bankTransferRequestData.requestedAt.toDate();
    const deadline = new Date(requestedAtDate);
    deadline.setDate(deadline.getDate() + 7);

    const formatDate = (date) =>
      date.toLocaleDateString("ja-JP", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });

    const formatAmount = (amount) => {
      if (!amount) return "価格未設定";
      if (typeof amount === "number") {
        return `¥${amount.toLocaleString()}`;
      }
      const parsed = Number(amount.replace(/[^\d]/g, ""));
      return parsed ? `¥${parsed.toLocaleString()}` : "価格未設定";
    };

    return (
      <Box sx={{ mt: 4, textAlign: "center" }}>
        <Typography variant="h6" color="primary" fontWeight="bold">
          銀行振込での申込みを受け付けています。
        </Typography>

        <Typography variant="body2" sx={{ mt: 1 }}>
          申込日: {formatDate(requestedAtDate)}
        </Typography>

        <Typography variant="body2">
          振込期限: {formatDate(deadline)}
        </Typography>

        <Typography variant="body2" sx={{ mt: 2 }}>
          金額: {formatAmount(bankTransferRequestData.amount)}
        </Typography>

        <Box sx={{ mt: 3, textAlign: "left", maxWidth: 320, mx: "auto" }}>
          <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
            銀行名: GMOあおぞらネット銀行
          </Typography>
          <Typography variant="body2">支店名: 法人第二営業部</Typography>
          <Typography variant="body2">種目：普通</Typography>
          <Typography variant="body2">口座番号: 1007963</Typography>
          <Typography variant="body2">
            口座名義: ファミュニティリンク株式会社イベント
          </Typography>
        </Box>

        <Box mt={3}>
          <Typography
            variant="h6"
            sx={{ color: "red", fontWeight: "bold", textAlign: "center" }}
          >
            振込名義: {bankTransferRequestData.formattedPhone} お名前
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontWeight: "bold",
              color: "red",
              textAlign: "center",
              fontSize: "1rem",
              backgroundColor: "yellow",
              p: 2,
              borderRadius: 1,
              mt: 2,
            }}
          >
            振込名義が異なりますと、入金確認に支障が生じる可能性がございます。
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ mt: 3 }}>
          クレジットカードでの支払いは無効化されています。
        </Typography>

        <Typography
          variant="body2"
          sx={{ mt: 4, color: "error.main", fontWeight: "bold" }}
        >
          振込期限を過ぎてのお振込みは、キャンセルの後返金させていただきます。
          その場合、返金手数料はお客様のご負担となります。ご注意ください。
        </Typography>
        <Typography
          variant="body2"
          sx={{ mt: 4, color: "error.main", fontWeight: "bold" }}
        >
          また、ご入金後、お客様のご都合によるキャンセルについては、返金を承ることができません。あらかじめご了承ください。
        </Typography>
        <Button
          variant="outlined"
          color="error"
          sx={{ mt: 4 }}
          onClick={() => setCancelDialogOpen(true)}
        >
          銀行振込をキャンセルする
        </Button>

        {/* 振込キャンセル */}
        <Dialog
          open={cancelDialogOpen}
          onClose={() => setCancelDialogOpen(false)}
        >
          <DialogTitle>銀行振込のキャンセル確認</DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              以下の点をご確認ください。
            </Typography>
            <Typography variant="body2" color="error">
              ・このキャンセルは取り消せません。
              <br />
              ・この申込みはいかなる理由があっても復元できません。
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={cancelConfirmed}
                  onChange={(e) => setCancelConfirmed(e.target.checked)}
                />
              }
              label="上記の内容を確認しました。"
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCancelDialogOpen(false)}>閉じる</Button>
            <Button
              variant="contained"
              color="error"
              disabled={!cancelConfirmed}
              onClick={handleCancelBankTransfer}
            >
              銀行振込をキャンセルする
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }


  const isOverseas = userData.prefecture?.includes("海外");

  const hasPayment = userData.payments?.some(
    (payment) => payment.paymentPhase === paymentPhase
  );

  const formattedPhone = userData.registrationPhone
    ? userData.registrationPhone.slice(-4)
    : "----";

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 2,
        justifyContent: "center",
      }}
    >
      {/* 銀行振込期限切れ自動キャンセル */}
      {isBankTransferExpired && expiredDeadlineDate && (
        <Box
          sx={{
            backgroundColor: "#fff3e0",
            borderLeft: "6px solid #ff9800",
            p: 2,
            mb: 3,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" color="error" fontWeight="bold">
            銀行振込の申請は期限切れとなりました
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            あなたが申込まれた銀行振込は、
            <strong>
              {expiredDeadlineDate.toLocaleString("ja-JP", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </strong>
            に振込期限を迎えたため、自動的にキャンセルされました。
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            再度お申込みされる場合は、もう一度お支払い手続きをお願いいたします。
          </Typography>
        </Box>
      )}

      {/* 支払いたち */}
      {dateRanges
        .filter((range) => range.visible !== false)
        .map((range, index) => {
          const isInRange =
            now >= range.start && (range.end ? now <= range.end : true);
          const isActive = isInRange && range.visible !== false && !hasPayment;
          const price = isOverseas
            ? `¥${Number(range.priceTaxExcluded).toLocaleString()}`
            : `¥${Number(range.priceTaxIncluded).toLocaleString()}`;
          const priceId = isOverseas
            ? range.priceIdTaxExcluded
            : range.priceIdTaxIncluded;
          const displayStart = range.start.toLocaleDateString("ja-JP");
          const displayEnd = range.end
            ? range.end.toLocaleDateString("ja-JP")
            : "期限なし";

          return (
            <Box
              key={index}
              sx={{
                maxWidth: "100%",
                borderRadius: 2,
                textAlign: "center",
                backgroundColor: "#fff",
                opacity: isActive ? 1 : 0.5,
                pointerEvents: isActive ? "auto" : "none",
                boxShadow: isActive ? 3 : 0,
                transition: "opacity 0.3s",
              }}
            >
              <StripeCheckoutButton2025
                userId={userData.userId}
                priceId={priceId}
                price={price}
                title={title}
                description={description}
                paymentPhase={paymentPhase}
                hasPayment={hasPayment}
                formattedPhone={formattedPhone}
                displayStart={displayStart}
                displayEnd={displayEnd}
                paymentPhaseId={range.id}
                onBankTransferRequestComplete={checkBankTransferRequest}
              />
            </Box>
          );
        })}
    </Box>
  );
};

export default PaymentContainer2025Late;
